import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import Select from 'react-select';
import { Tappable } from '@common/ui/general';
import { gtmEventHandler } from '@common/utils/gtm';
import closeIcon from '@alumni_v2/images/svg/cross-icon.svg';

import styles from './SelectField.module.scss';


const filterNameMapping = {
  currentCompany: 'Current Company',
  currentStatus: 'Current Role',
  targetRole: 'Target Role',
  targetCompany: 'Target Company',
  currentCollege: 'Current College',
};


const SelectField = ({
  id,
  containerClass,
  options,
  selectedOption,
  setSelectedOption,
  focusId,
  setFocusId,
  title = '',
  placeholderText = 'Add Role',
  placeholderActiveText = '| Search role',
}) => {
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const trackActivity = useCallback((eventName, sectionName, extraInfo) => {
    gtmEventHandler(
      'cta',
      eventName,
      'click',
      'alumni_directory',
      {
        click_type: eventName,
        click_section: sectionName,
        click_text: filterNameMapping[id],
        gtm_custom_attributes: extraInfo,
      },
    );
  }, [id]);

  const handleChange = useCallback(
    (option) => {
      setSelectedOption(option);
      trackActivity('select_filter', 'filter_section_dropdown', {
        value: option?.label,
      });
    },
    [setSelectedOption, trackActivity],
  );
  const handleFocus = useCallback(() => {
    if (focusId !== id) setFocusId(id);
  }, [focusId, id, setFocusId]);

  const handleBlur = useCallback(() => {
    if (focusId === id) setFocusId(null);
  }, [focusId, id, setFocusId]);

  const handleOuterClick = useCallback(() => {
    if (focusId !== id) {
      setFocusId(id);
      setIsDropDownOpen(true);
    }
  }, [focusId, id, setFocusId]);

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      background: 'none',
      minHeight: 0,
      padding: 0,
      cursor: 'pointer',
      '&:hover': {
        border: 'none',
      },
    }),
    menu: (base) => ({
      ...base,
      background: '#212121',
      boxShadow: 'none',
      width: '40rem',
      position: 'absolute',
      left: '-2.5rem',
      top: '5rem',
      overflowY: 'scroll',
      cursor: 'pointer',
      scrollbarWidth: 'none',
      '-ms-overflow-style': 'none',
    }),
    menuList: (base) => ({
      ...base,
      overflowY: 'scroll',
      scrollbarWidth: 'none',
      cursor: 'pointer',
      '-ms-overflow-style': 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: 'rgba(212, 212, 212, 1)',
    }),
    input: (base) => ({
      ...base,
      color: 'rgba(212, 212, 212, 1)',
    }),
    option: (base, state) => ({
      ...base,
      // eslint-disable-next-line no-nested-ternary
      background: state.isSelected
        ? '#171717'
        : state.isFocused
          ? '#2B2B2B'
          : '#212121',
      color: 'rgba(212, 212, 212, 1)',
      '&:active': {
        background: '#171717',
      },
      padding: '1.2rem 2.4rem',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: 'rgba(255, 255, 255, 1)',
      display: 'inline-block',
      '& #optionsLabel': {
        display: 'none',
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0',
    }),
  };


  return (
    <div
      role="button"
      tabIndex="0"
      className={classNames(
        styles.formFields,
        containerClass,
        { [styles.focusedField]: focusId === id },
      )}
      onClick={handleOuterClick}
    >
      <div className={styles.selectFieldTitle}>
        <div className={styles.fieldTitle}>{title}</div>
        {selectedOption[id]
          && (
            <Tappable
              onClick={(event) => {
                event.stopPropagation();
                trackActivity('remove_selected_filter', 'filter_section');
                setSelectedOption(null);
              }}
            >
              <img
                src={closeIcon}
                alt="closeIcon"
                className={styles.crossButton}
              />
            </Tappable>
          )}
      </div>
      <Select
        onMenuClose={() => setIsDropDownOpen(false)}
        onMenuOpen={() => {
          setIsDropDownOpen(true);
          trackActivity('open_filter_dropdown', 'filter_section');
        }}
        menuIsOpen={isDropDownOpen}
        value={selectedOption[id]}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        isSearchable
        options={options}
        getOptionLabel={(e) => (
          <div className={styles.dropDownOptions}>
            <span
              className={classNames(
                styles.optionsValue,
                { [styles.disabledOption]: e.value === 'WORK ROLE' },
              )}
            >
              {e.value}
            </span>
            <span className={styles.optionsLabel} id="optionsLabel">
              {e.label}
            </span>
          </div>
        )}
        placeholder={isDropDownOpen ? placeholderActiveText : placeholderText}
        className={styles.fieldSelect}
        styles={customSelectStyles}
      />
    </div>
  );
};

export default SelectField;
